import { useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TextField,
  Box,
  TablePagination,
} from "@mui/material";
import { Shareholder } from "../../types/Shareholders";
import { TEXT_SECONDARY } from "../colors";

const ROWS_PER_PAGE = 25;
const NUMBER_FORMAT = new Intl.NumberFormat("en-GB");

// A genuinely tiny holding (e.g. 0.003%) rounds to "0.00%" at 2dp, which
// reads as "no shares at all" rather than "a very small amount" - which is
// wrong, since it's already filtered to positive holdings only by this point.
const formatPercent = (percent: number) => (percent > 0 && percent < 0.01 ? "<0.01%" : `${percent.toFixed(2)}%`);

type SortKey = "name" | "class" | "shares" | "percent";

export const ShareholdersTable = (props: { shareholders: Shareholder[]; showClass: boolean }) => {
  const { shareholders, showClass } = props;
  const [filter, setFilter] = useState("");
  const [orderBy, setOrderBy] = useState<SortKey>("shares");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);

  const visible = useMemo(() => {
    let result = shareholders;

    if (filter) {
      const lower = filter.toLowerCase();
      result = result.filter((s) => s.name.toLowerCase().includes(lower));
    }

    return [...result].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return order === "asc" ? comparison : -comparison;
    });
  }, [shareholders, filter, orderBy, order]);

  const handleSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const paged = visible.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  return (
    <Box style={{ padding: 10 }}>
      {shareholders.length > 10 && (
        <Box style={{ padding: "20px 10px 10px" }}>
          <TextField
            label="Filter by name"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(0);
            }}
            fullWidth
            size="small"
          />
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel active={orderBy === "name"} direction={orderBy === "name" ? order : "asc"} onClick={() => handleSort("name")}>
                Name
              </TableSortLabel>
            </TableCell>
            {showClass && (
              <TableCell>
                <TableSortLabel active={orderBy === "class"} direction={orderBy === "class" ? order : "asc"} onClick={() => handleSort("class")}>
                  Class
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell align="right">
              <TableSortLabel active={orderBy === "shares"} direction={orderBy === "shares" ? order : "asc"} onClick={() => handleSort("shares")}>
                Shares
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel active={orderBy === "percent"} direction={orderBy === "percent" ? order : "asc"} onClick={() => handleSort("percent")}>
                %
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paged.map((s, index) => (
            <TableRow key={`${s.name}-${s.class}-${index}`}>
              <TableCell style={{ color: TEXT_SECONDARY }}>{s.name}</TableCell>
              {showClass && <TableCell>{s.class}</TableCell>}
              <TableCell align="right">{NUMBER_FORMAT.format(s.shares)}</TableCell>
              <TableCell align="right">{formatPercent(s.percent)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={visible.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
      />
    </Box>
  );
};

export default ShareholdersTable;
